import { useEffect, useMemo, useState } from 'react';
import { getCurrentOrganizationContext, getOrganizationMemberships } from './organizationContext';

const PREFERRED_ORGANIZATION_STORAGE_KEY = 'platform-preferred-organization-id';
const ORGANIZATION_SELECTION_EVENT = 'platform-organization-selection-change';

export function getPreferredOrganizationId() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(PREFERRED_ORGANIZATION_STORAGE_KEY) || '';
}

export function setPreferredOrganizationId(organizationId = '') {
  if (typeof window === 'undefined') return;

  if (organizationId) {
    window.localStorage.setItem(PREFERRED_ORGANIZATION_STORAGE_KEY, organizationId);
  } else {
    window.localStorage.removeItem(PREFERRED_ORGANIZATION_STORAGE_KEY);
  }

  window.dispatchEvent(
    new CustomEvent(ORGANIZATION_SELECTION_EVENT, {
      detail: {
        organizationId: organizationId || '',
      },
    })
  );
}

export function resolveSelectedOrganization(userData = {}, options = {}) {
  const memberships = getOrganizationMemberships(userData, {
    organizationMembers: options.organizationMembers || [],
    organizationsById: options.organizationsById || {},
  });

  const preferredOrganizationId = options.preferredOrganizationId || getPreferredOrganizationId();
  const selected =
    getCurrentOrganizationContext(userData, preferredOrganizationId, {
      organizationMembers: options.organizationMembers || [],
      organizationsById: options.organizationsById || {},
    }) ||
    memberships[0] ||
    null;

  return {
    memberships,
    selectedOrganization: selected,
    selectedOrganizationId: selected?.organizationId || '',
  };
}

export function useCurrentOrganizationSelection(userData = {}, options = {}) {
  const [preferredOrganizationId, setPreferredOrganizationIdState] = useState(() =>
    options.preferredOrganizationId || getPreferredOrganizationId()
  );

  const resolved = useMemo(
    () =>
      resolveSelectedOrganization(userData, {
        ...options,
        preferredOrganizationId,
      }),
    [options, preferredOrganizationId, userData]
  );

  useEffect(() => {
    if (resolved.selectedOrganizationId) {
      setPreferredOrganizationId(resolved.selectedOrganizationId);
    }
  }, [resolved.selectedOrganizationId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleStorageChange = () => {
      setPreferredOrganizationIdState(getPreferredOrganizationId());
    };

    const handleSelectionChange = (event) => {
      setPreferredOrganizationIdState(event?.detail?.organizationId || getPreferredOrganizationId());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(ORGANIZATION_SELECTION_EVENT, handleSelectionChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(ORGANIZATION_SELECTION_EVENT, handleSelectionChange);
    };
  }, []);

  return {
    ...resolved,
    preferredOrganizationId,
    setSelectedOrganizationId: (organizationId) => {
      setPreferredOrganizationIdState(organizationId || '');
      setPreferredOrganizationId(organizationId || '');
    },
  };
}

export function useSelectedOrganizationContext(userData = {}, options = {}) {
  return useCurrentOrganizationSelection(userData, options);
}
